import { useCallback, useEffect, useMemo, useState } from 'react';
import { FiChevronLeft, FiFolder, FiList, FiMusic } from 'react-icons/fi';
import { storageBoxApi } from '../../../storageBox/api/storageBoxApi';
import { isPreviewableAudio } from './audioFormatUtils';

function filterBrowseItems(items) {
  return (items || []).filter(
    (item) => item.type === 'folder' || (item.type === 'file' && isPreviewableAudio(item.mimeType, item.name))
  );
}

/**
 * Lightweight Storage Box folder browser — lists folders and audio files only.
 */
export default function StorageBoxAudioBrowser({
  onSelectFile,
  onQueuePlayNext,
  className = '',
}) {
  const [stack, setStack] = useState([{ id: null, name: 'My drive' }]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const current = stack[stack.length - 1];

  const loadFolder = useCallback(async (parentId) => {
    setLoading(true);
    setError('');
    try {
      const data = await storageBoxApi.listItems(parentId);
      setItems(filterBrowseItems(data.items));
    } catch (e) {
      setItems([]);
      setError(e.message || 'Could not load Storage Box');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadFolder(current.id);
  }, [current.id, loadFolder]);

  const breadcrumb = useMemo(() => stack.map((s) => s.name).join(' / '), [stack]);

  const openFolder = (folder) => {
    setStack((prev) => [...prev, { id: folder.itemId, name: folder.name }]);
  };

  const goUp = () => {
    if (stack.length <= 1) return;
    setStack((prev) => prev.slice(0, -1));
  };

  return (
    <div className={`sb-audio-browser ${className}`.trim()}>
      <div className="sb-audio-browser__toolbar">
        <button type="button" className="audio-player__secondary" onClick={goUp} disabled={stack.length <= 1}>
          <FiChevronLeft aria-hidden /> Up
        </button>
        <span className="sb-audio-browser__crumb" title={breadcrumb}>
          {breadcrumb}
        </span>
      </div>
      {error ? <p className="sb-audio-browser__empty">{error}</p> : null}
      {loading ? <p className="sb-audio-browser__empty">Loading…</p> : null}
      {!loading && !error && items.length === 0 ? (
        <p className="sb-audio-browser__empty">No folders or audio files here.</p>
      ) : null}
      <ul className="sb-audio-browser__list">
        {items.map((item) => (
          <li key={item.itemId} className="sb-audio-browser__item">
            <button
              type="button"
              className="sb-audio-browser__row"
              onClick={() => {
                if (item.type === 'folder') openFolder(item);
                else onSelectFile?.(item);
              }}
            >
              {item.type === 'folder' ? <FiFolder aria-hidden /> : <FiMusic aria-hidden />}
              <span className="sb-audio-browser__row-name">{item.name}</span>
              <span className="sb-audio-browser__row-meta">
                {item.type === 'folder' ? 'Folder' : item.mimeType || 'Audio'}
              </span>
            </button>
            {item.type === 'file' && onQueuePlayNext ? (
              <button
                type="button"
                className="sb-audio-browser__queue-btn"
                title="Play next in queue"
                aria-label={`Play next in queue: ${item.name}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onQueuePlayNext(item);
                }}
              >
                <FiList aria-hidden />
              </button>
            ) : null}
          </li>
        ))}
      </ul>
    </div>
  );
}
